import React, { useState } from "react";
import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import AiChat from "pages/SuperAdminPages/AiImamPage/AiChat";
import {
  Badge,
  Button,
  Card,
  Icon,
  PageHeader,
  Select,
  styles as ui,
  TextField,
  Toggle,
  useConfirm,
} from "stories/Admin";
import styles from "./AiBonnPrompterPage.module.scss";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  meta?: {
    intent?: string;
    usedFastPath?: boolean;
    conversationFollowUp?: boolean;
    engine?: string | null;
    agentEnabledOnServer?: boolean | null;
    agentError?: string | null;
  };
}

interface MosqueItem {
  orgNr: string;
  name: string;
  address?: string;
  subtitle?: string;
  rank?: number;
}

interface DebugChatResponse {
  answer: string;
  intent: string;
  usedFastPath: boolean;
  conversationFollowUp: boolean;
  contextJson: string;
  mosqueItems?: MosqueItem[] | null;
  /** «agent» = ny AiBønn med verktøy, «legacy» = gammel. Mangler = backend før denne versjonen. */
  engine?: string | null;
  agentEnabledOnServer?: boolean | null;
  /** Satt når ny AiBønn feilet og gammel tok over. */
  agentError?: string | null;
  timestamp: string;
}

type EngineChoice = "server" | "agent" | "legacy";

const engineLabel = (meta: ChatMessage["meta"]): string => {
  if (!meta) return "";
  if (!meta.engine) return " · gammel backend-versjon (ikke deployet)";
  const engine = meta.engine === "agent" ? "ny AiBønn" : "gammel AiBønn";
  const flag =
    meta.agentEnabledOnServer == null
      ? ""
      : meta.agentEnabledOnServer
        ? " · app: ny"
        : " · app: gammel";
  return ` · ${engine}${flag}`;
};

const AiBonnPrompterPage: React.FC = () => {
  const { execute } = useFetch();
  const confirm = useConfirm();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastContextJson, setLastContextJson] = useState<string | null>(null);
  const [lastMosqueItems, setLastMosqueItems] = useState<MosqueItem[]>([]);
  const [showDebug, setShowDebug] = useState(true);
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [locationId, setLocationId] = useState("");
  const [mosqueOrgNr, setMosqueOrgNr] = useState("");
  const [date, setDate] = useState("");
  const [engineChoice, setEngineChoice] = useState<EngineChoice>("server");
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userQuestion = input.trim();
    setInput("");

    const newUserMessage: ChatMessage = {
      role: "user",
      content: userQuestion,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newUserMessage]);
    setLoading(true);

    const history = [...messages, newUserMessage].map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const body: Record<string, unknown> = {
      question: userQuestion,
      history,
    };
    if (lat.trim()) body.lat = Number(lat);
    if (lon.trim()) body.lon = Number(lon);
    if (locationId.trim()) body.locationId = Number(locationId);
    if (mosqueOrgNr.trim()) body.mosqueOrgNr = mosqueOrgNr.trim();
    if (date.trim()) body.date = date.trim();

    try {
      const result = (await execute(
        engineChoice === "server"
          ? "/api/v1/admin/ai-prayer-times/debug-chat"
          : `/api/v1/admin/ai-prayer-times/debug-chat?agent=${engineChoice === "agent"}`,
        "POST",
        body,
      )) as DebugChatResponse | null;

      if (result?.answer) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: result.answer,
            timestamp: new Date(result.timestamp || Date.now()),
            meta: {
              intent: result.intent,
              usedFastPath: result.usedFastPath,
              conversationFollowUp: result.conversationFollowUp,
              engine: result.engine,
              agentEnabledOnServer: result.agentEnabledOnServer,
              agentError: result.agentError,
            },
          },
        ]);
        setLastContextJson(result.contextJson || null);
        setLastMosqueItems(result.mosqueItems || []);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Beklager, jeg kunne ikke svare på det akkurat nå.",
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error) {
      console.error("AiBønn prompter error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Det oppstod en feil under kommunikasjon med AiBønn.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    if (
      await confirm({
        title: "Slette samtalen?",
        message: "Hele samtalen og debug-informasjonen fjernes.",
        confirmLabel: "Slett samtale",
        tone: "danger",
      })
    ) {
      setMessages([]);
      setLastContextJson(null);
      setLastMosqueItems([]);
    }
  };

  const fillOsloGps = () => {
    setLat("59.9139");
    setLon("10.7522");
  };

  const renderMeta = (meta: ChatMessage["meta"]) =>
    meta && (
      <>
        <span className={styles.meta}>
          {meta.intent && <Badge tone="grey">intent: {meta.intent}</Badge>}
          {meta.usedFastPath ? (
            <Badge tone="mint">fast-path</Badge>
          ) : (
            <Badge tone="gold">OpenAI</Badge>
          )}
          <Badge tone="blue">{engineLabel(meta).replace(/^ · /, "")}</Badge>
          {meta.conversationFollowUp && <Badge tone="lilac">oppfølging</Badge>}
        </span>
        {meta.agentError && (
          <span className={styles.agentError}>
            Ny AiBønn feilet: {meta.agentError}
          </span>
        )}
      </>
    );

  const contextText = lastContextJson
    ? (() => {
        try {
          return JSON.stringify(JSON.parse(lastContextJson), null, 2);
        } catch {
          return lastContextJson;
        }
      })()
    : null;

  return (
    <>
      <PageHeader
        title="Test og AI-imam"
        subtitle="Test AiBønn med samme kontekst som appen, eller chat med AI-imamen."
        actions={
          <>
            <Toggle
              label={<span className={styles.toggleLabel}>Vis debug</span>}
              checked={showDebug}
              onChange={(e) => setShowDebug(e.target.checked)}
            />
            <Button
              variant="secondary"
              onClick={clearChat}
              disabled={messages.length === 0}
              icon={<Icon name="trash" size={16} strokeWidth={2} />}
            >
              Slett samtale
            </Button>
          </>
        }
      />
      <SectionTabs />

      <Card title="Simuler app-kontekst (valgfritt)">
        <div className={styles.contextGrid}>
          <TextField
            label="lat"
            placeholder="59.9139"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
          />
          <TextField
            label="lon"
            placeholder="10.7522"
            value={lon}
            onChange={(e) => setLon(e.target.value)}
          />
          <TextField
            label="locationId"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          />
          <TextField
            label="mosqueOrgNr"
            value={mosqueOrgNr}
            onChange={(e) => setMosqueOrgNr(e.target.value)}
          />
          <TextField
            label="Dato"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Select
            label="Hvem svarer"
            value={engineChoice}
            onChange={(e) => setEngineChoice(e.target.value as EngineChoice)}
          >
            <option value="server">Som appen</option>
            <option value="agent">Ny AiBønn</option>
            <option value="legacy">Gammel AiBønn</option>
          </Select>
          <Button
            variant="secondary"
            onClick={fillOsloGps}
            title="Fyll inn GPS for Oslo"
            icon={<Icon name="pin" size={16} strokeWidth={2} />}
          >
            Oslo
          </Button>
        </div>
      </Card>

      <div className={`${styles.main} ${showDebug ? styles.withDebug : ""}`}>
        <AiChat
          messages={messages.map((msg) => ({
            fromUser: msg.role === "user",
            content: msg.content,
            timestamp: msg.timestamp,
            meta: renderMeta(msg.meta),
          }))}
          loading={loading}
          input={input}
          onInput={setInput}
          onSend={handleSend}
          placeholder="F.eks. «Når er fajr i Oslo?» eller «Hva er Mawaqeet?»"
          emptyTitle="AiBønn-test"
          emptyText="Still spørsmål om adhan, jamat, jummah og moskeer. Samme backend som i appen. Debug-panelet viser intent og kontekst."
        />

        {showDebug && (
          <Card title="Debug">
            {lastMosqueItems.length > 0 && (
              <div className={styles.debugSection}>
                <span className={ui.lbl}>mosqueItems</span>
                {lastMosqueItems.map((item) => (
                  <span
                    key={`${item.orgNr}-${item.rank}`}
                    className={styles.mosqueItem}
                  >
                    {item.rank ? <b className={ui.num}>{item.rank}</b> : null}
                    <span className={styles.mosqueName}>
                      {item.name}
                      {item.address ? (
                        <span className={ui.help}> ({item.address})</span>
                      ) : null}
                    </span>
                    {item.subtitle && (
                      <span className={styles.mosqueSub}>{item.subtitle}</span>
                    )}
                  </span>
                ))}
              </div>
            )}
            <div className={styles.debugSection}>
              <span className={ui.lbl}>contextJson</span>
              {contextText ? (
                <pre className={styles.pre}>{contextText}</pre>
              ) : (
                <span className={ui.help}>
                  Ingen kontekst ennå. Send et spørsmål.
                </span>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default AiBonnPrompterPage;
